import React, { useEffect, useState } from "react";
import {
  Result,
  Input,
  Button,
  Space,
  Typography,
  message,
  Divider,
} from "antd";
import {
  LoadingOutlined,
  LockOutlined,
  LockTwoTone,
  SyncOutlined,
} from "@ant-design/icons";
import { useIdentitySystem } from "../../framework/identity";
import { extractDiskInfo, wrapAuthStringOrHeader } from "../../api/helpers";
import { Link } from "react-router-dom";
import useScreenType from "react-screentype-hook";
import { generateDeterministicMnemonic } from "../../api/icp";
import toast from "react-hot-toast";

const { Text } = Typography;
const { Password } = Input;

interface DirectoryGuardProps {
  onPasswordSubmit?: (password: string) => void;
  resourceID: string;
  loading: boolean;
  fetchResource: () => void;
}

const DirectoryGuard: React.FC<DirectoryGuardProps> = ({
  resourceID,
  loading,
  fetchResource,
}) => {
  const [password, setPassword] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const screenType = useScreenType();
  const {
    currentOrg,
    wrapOrgCode,
    deriveProfileFromSeed,
    hydrateFullAuthProfile,
    generateSignature,
    createProfile,
    switchProfile,
  } = useIdentitySystem();

  const handleSubmit = async () => {
    if (!password || !currentOrg) return;
    setIsLoading(true);
    const deterministic_seed_phrase = generateDeterministicMnemonic(
      `${password}-${resourceID}`
    );
    const newProfile = await deriveProfileFromSeed(deterministic_seed_phrase);

    const auth_profile = await hydrateFullAuthProfile(newProfile, true);
    const auth_token = await generateSignature(auth_profile);

    console.log(`resourceID====`, resourceID);

    try {
      const action = {
        action: resourceID.startsWith("FolderID_") ? "GET_FOLDER" : "GET_FILE",
        payload: {
          id: resourceID,
        },
      };
      const { url, headers } = wrapAuthStringOrHeader(
        `${currentOrg.host}/v1/drive/${currentOrg.driveID}/directory/action`,
        {
          "Content-Type": "application/json",
        },
        auth_token
      );
      const check_response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({
          actions: [action],
        }),
      });
      const res = await check_response.json();

      if (!res || !res[0].response.result) {
        toast.error(<span>Invalid password</span>);
      } else {
        toast.success(<span>Valid password, redirecting...</span>);
        // Create the profile
        const newProfile = await createProfile({
          icpPublicAddress: auth_profile.icpPublicKey,
          evmPublicAddress: auth_profile.evmPublicKey,
          seedPhrase: deterministic_seed_phrase,
          note: `Password temp login for ${currentOrg.driveID}`,
          avatar: "",
          nickname: "Temp Password",
        });
        // Switch to the new profile
        await switchProfile(newProfile);
        // refresh page
        window.location.reload();
      }
      setIsLoading(false);
    } catch (e) {
      console.log(`err`, e);
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  const { diskTypeEnum, diskID } = extractDiskInfo();

  return (
    <div style={{ marginTop: screenType.isMobile ? 16 : 48 }}>
      <Result
        icon={
          screenType.isMobile ? (
            <LockTwoTone style={{ fontSize: "64px" }} />
          ) : undefined
        }
        status={screenType.isMobile ? undefined : "403"}
        title={
          <Space direction="vertical" size="large" style={{ gap: 8 }}>
            <span style={{ fontSize: "0.9rem" }}>
              {loading ? (
                <span>
                  <LoadingOutlined />
                  <i style={{ marginLeft: 8, color: "rgba(0,0,0,0.2)" }}>
                    Syncing
                  </i>
                </span>
              ) : (
                <span
                  onClick={() => {
                    toast(<span>Refetching...</span>);
                    fetchResource();
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <SyncOutlined
                    style={{ color: "rgba(0,0,0,0.2)", fontSize: "1.1rem" }}
                  />
                  <i style={{ marginLeft: 8, color: "rgba(0,0,0,0.2)" }}>
                    Check Again
                  </i>
                </span>
              )}
            </span>
            <span style={{ fontSize: "1.7rem" }}>Unauthorized</span>
          </Space>
        }
        subTitle={
          <span>
            You do not have permission to view this{" "}
            {resourceID.startsWith("FolderID_") ? "folder" : "file"}. Click
            "Check Again" just in case old cache.
          </span>
        }
        extra={
          <div style={{ maxWidth: 400, margin: "0 auto" }}>
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <Text>If you have a temporary password you can try it below</Text>

              <Space.Compact style={{ width: "100%" }}>
                <Password
                  prefix={<LockOutlined />}
                  placeholder="Enter temporary password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                  allowClear
                />
                <Button
                  type="primary"
                  loading={isLoading}
                  onClick={handleSubmit}
                  disabled={!password}
                >
                  Enter
                </Button>
              </Space.Compact>
            </Space>
            <Divider />
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <Text style={{ color: "rgba(0,0,0,0.3)" }}>
                Or see what you have access to
              </Text>
              <Link
                to={wrapOrgCode(
                  `/drive/${diskTypeEnum}/${diskID}/shared-with-me`
                )}
              >
                <Button type="primary">View Shared with Me</Button>
              </Link>
            </Space>
            <br />
            <br />
            <br />
            <br />
            <br />
            <br />
            <span style={{ color: "rgba(0,0,0,0.1)" }}>OfficeX</span>
          </div>
        }
      />
    </div>
  );
};

export default DirectoryGuard;
